// Historical sources and helpers for Bible books

// Books that have Dead Sea Scrolls fragments
export const DEAD_SEA_SCROLLS_BOOKS = new Set([
  "Genesis",
  "Exodus",
  "Leviticus",
  "Numbers",
  "Deuteronomy",
  "Joshua",
  "Judges",
  "1 Samuel",
  "2 Samuel",
  "1 Kings",
  "2 Kings",
  "Isaiah",
  "Jeremiah",
  "Ezekiel",
  "Hosea",
  "Joel",
  "Amos",
  "Obadiah",
  "Jonah",
  "Micah",
  "Nahum",
  "Habakkuk",
  "Zephaniah",
  "Haggai",
  "Zechariah",
  "Malachi",
  "Psalms",
  "Proverbs",
  "Job",
  "Song of Songs",
  "Ruth",
  "Lamentations",
  "Ecclesiastes",
  "Daniel",
  "Ezra",
  "Nehemiah",
  "1 Chronicles",
  "2 Chronicles",
]);

export class HistoricalDocument {
  constructor(name, year, getLink, isValidBook = () => true, description = "") {
    this.name = name;
    this.year = year;
    this.getLink = getLink;
    this.isValidBook = isValidBook;
    this.description = description;
  }
  getDisplayName() {
    return `${this.name} (${this.year})`;
  }
}

export const CODEX_BOOK_IDS = {
  Genesis: 1,
  Leviticus: 2,
  Numbers: 3,
  Deuteronomy: 4,
  Joshua: 5,
  Judges: 6,
  "1 Chronicles": 70,
  "2 Esdras": 8,
  Esther: 9,
  Tobit: 10,
  Judith: 11,
  "1 Maccabees": 12,
  "4 Maccabees": 13,
  Isaiah: 14,
  Jeremiah: 15,
  Lamentations: 16,
  Joel: 17,
  Obadiah: 18,
  Jonah: 19,
  Nahum: 20,
  Habakkuk: 21,
  Zephaniah: 22,
  Haggai: 23,
  Zechariah: 24,
  Malachi: 25,
  Psalms: 26,
  Proverbs: 27,
  Ecclesiastes: 28,
  "Song of Songs": 29,
  "Wisdom of Solomon": 30,
  Sirach: 31,
  Job: 32,
  Matthew: 33,
  Mark: 34,
  Luke: 35,
  John: 36,
  Romans: 37,
  "1 Corinthians": 38,
  "2 Corinthians": 39,
  Galatians: 40,
  Ephesians: 41,
  Philippians: 42,
  Colossians: 43,
  "1 Thessalonians": 44,
  "2 Thessalonians": 45,
  Hebrews: 46,
  "1 Timothy": 47,
  "2 Timothy": 48,
  Titus: 49,
  Philemon: 50,
  Acts: 51,
  James: 52,
  "1 Peter": 53,
  "2 Peter": 54,
  "1 John": 55,
  "2 John": 56,
  "3 John": 57,
  Jude: 58,
  Revelation: 59,
  Barnabas: 60,
  "Shepherd of Hermas": 61,
};

export const VATICANUS_BOOK_MAP = {
  Genesis: 1,
  Exodus: 47,
  Leviticus: 99,
  Numbers: 138,
  Deuteronomy: 191,
  Joshua: 238,
  Judges: 271,
  Ruth: 304,
  "1 Samuel": 309,
  "2 Samuel": 354,
  "1 Kings": 395,
  "2 Kings": 442,
  "1 Chronicles": 484,
  "2 Chronicles": 522,
  Psalms: 625,
  Proverbs: 714,
  Ecclesiastes: 750,
  "Song of Songs": 763,
  Job: 769,
  Esther: 893,
  Judith: 908,
  Tobit: 930,
  Hosea: 945,
  Amos: 954,
  Micah: 962,
  Joel: 968,
  Obadiah: 972,
  Jonah: 973,
  Nahum: 976,
  Habakkuk: 978,
  Zephaniah: 981,
  Haggai: 984,
  Zechariah: 987,
  Malachi: 999,
  Isaiah: 1002,
  Jeremiah: 1064,
  Lamentations: 1133,
  Ezekiel: 1143,
  Daniel: 1206,
  Matthew: 1235,
  Mark: 1277,
  Luke: 1304,
  John: 1349,
  Acts: 1382,
  James: 1426,
  "1 Peter": 1430,
  "2 Peter": 1434,
  "1 John": 1437,
  "2 John": 1442,
  "3 John": 1442,
  Jude: 1443,
  Romans: 1445,
  "1 Corinthians": 1461,
  "2 Corinthians": 1477,
  Galatians: 1488,
  Ephesians: 1493,
  Philippians: 1499,
  Colossians: 1502,
  "1 Thessalonians": 1506,
  "2 Thessalonians": 1510,
  Hebrews: 1512,
  Revelation: 1523,
};

export const HISTORICAL_DOCUMENTS = [
  new HistoricalDocument(
    "Dead Sea Scrolls",
    "ca. 250 BCE-50 CE",
    (bookName, chapter, verse) => {
      return `https://www.deadseascrolls.org.il/explore-the-archive/search#q='${encodeURIComponent(
        bookName
      )}'`;
    },
    (bookName) => DEAD_SEA_SCROLLS_BOOKS.has(bookName),
    "Ancient Jewish manuscripts discovered in the Qumran caves, containing the oldest known copies of biblical texts"
  ),
  new HistoricalDocument(
    "Codex Vaticanus",
    "ca. 325-350 CE",
    (bookName, chapter, verse) => {
      const folio = VATICANUS_BOOK_MAP[bookName];
      if (!folio) return null;
      return `https://digi.vatlib.it/view/MSS_Vat.gr.1209/${String(
        folio + 4
      ).padStart(4, "0")}`;
    },
    () => true,
    "One of the oldest complete copies of the Bible, written in Greek and housed in the Vatican Library"
  ),
  new HistoricalDocument(
    "Codex Sinaiticus",
    "ca. 330-360 CE",
    (bookName, chapter, verse) => {
      const bookId = CODEX_BOOK_IDS[bookName];
      if (!bookId) return null;
      let url = `https://www.codexsinaiticus.org/en/manuscript.aspx?book=${bookId}&chapter=${chapter}&side=r&lid=en`;
      if (verse) {
        url += `&verse=${verse}`;
      }
      return url;
    },
    () => true,
    "One of the oldest complete copies of the Bible, discovered at St. Catherine's Monastery in Sinai"
  ),
];

export const HIGHLIGHT_COLORS = [
  "#fff59d", // yellow
  "#90caf9", // blue
  "#a5d6a7", // green
  "#ffab91", // orange
  "#f48fb1", // pink
  "#ce93d8", // purple
];
export const DARK_HIGHLIGHT_COLORS = [
  "#e6b800", // amber
  "#4a6fa5", // muted blue
  "#4a8c4a", // muted green
  "e67700", // amber orange
  "#a35c7a", // muted pink
  "#8c5c99", // muted purple
];
